import React from "react";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import useCreateNairaWallet, {
  submitEmptyNairaWallet,
} from "../../hooks/useCreateNairaWallet";

export type NairaWalletModalVariant = "create" | "profile" | "kyc2";

type CreateNairaWalletModalProps = {
  variant?: NairaWalletModalVariant;
  onClose: () => void;
  onCompleteProfile?: () => void;
};

const COPY = {
  create: {
    title: "Create your Naira Wallet",
    description:
      "You're all set. Tap below to create your Naira wallet instantly.",
    cta: "Create Naira Wallet",
  },
  profile: {
    title: "Complete your profile to get Naira Wallet",
    description:
      "We need a few missing details before opening your Naira Wallet. These requirements can change based on compliance rules.",
    cta: "Complete Profile",
  },
  kyc2: {
    title: "Complete KYC Level 2 to get Naira Wallet",
    description:
      "You need to complete KYC Level 2 verification before you can create your Naira Wallet.",
    cta: "Complete KYC Level 2",
  },
} as const;

const CreateNairaWalletModal: React.FC<CreateNairaWalletModalProps> = ({
  variant = "create",
  onClose,
  onCompleteProfile,
}) => {
  const navigate = useNavigate();
  const copy = COPY[variant];
  const createNairaWallet = useCreateNairaWallet({ onSuccess: onClose });

  const handleCta = () => {
    if (variant === "kyc2") {
      onClose();
      navigate("/kyc-verification", { state: { openLevel2Flow: true } });
      return;
    }
    if (variant === "profile") {
      onClose();
      onCompleteProfile?.();
      return;
    }
    submitEmptyNairaWallet(createNairaWallet);
  };

  const isCreating = variant === "create" && createNairaWallet.isPending;

  return (
    <div
      className="fixed inset-0 z-[120] flex font-manrope lgss:items-center lgss:justify-center lgss:bg-black/80 lgss:backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex h-full w-full flex-col overflow-hidden bg-[#042741] text-white bg-wrappedBg bg-cover bg-center bg-no-repeat lgss:h-auto lgss:max-h-[90vh] lgss:w-full lgss:max-w-[min(100%,28rem)] lgss:aspect-[9/16] lgss:rounded-3xl lgss:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-[#042741]/30 via-[#042741]/50 to-[#042741]/85" />

        <div className="relative z-10 flex flex-1 flex-col items-center justify-center px-8 text-center">
          <h1 className="max-w-[320px] text-[28px] font-semibold font-sora leading-tight md:text-[32px]">
            {copy.title}
          </h1>
          <p className="mt-10 md:mt-12 max-w-[340px] text-[13px] font-medium font-manrope leading-relaxed text-[#F1F3F5] md:text-[14px]">
            {copy.description}
          </p>
        </div>

        <div className="relative z-10 w-full px-6 pb-10 pt-4 lgss:pb-8">
          <button
            type="button"
            onClick={handleCta}
            disabled={isCreating}
            className="flex h-[48px] w-full items-center justify-center rounded-[8px] bg-[#0A7BDE] text-[15px] font-semibold text-gray-50 transition hover:brightness-110 disabled:opacity-70"
          >
            {isCreating ? (
              <ClipLoader color="#FFFFFF" size={24} />
            ) : (
              copy.cta
            )}
          </button>
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          disabled={isCreating}
          aria-label="Close"
          className="absolute right-4 top-4 z-30 p-1 disabled:opacity-50"
        >
          <IoClose className="text-white text-[24px]" />
        </button>
      </div>
    </div>
  );
};

export default CreateNairaWalletModal;
